import { logger } from '@vagabondinternet/logging';
import { IDENTITY_PATHS, identityAuthUrl } from '@words-happen/app-urls';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type Player, useWhoAmIQuery, type WhoAmIResult } from '../api/whoami';
import { type BrowserLocation, windowLocation } from '../navigation';
import { storageAccountSettingsCache } from '../settings/account-settings-cache';
import { type AuthCookie, documentAuthCookie } from './auth-cookie';
import { createEndSession, type EndSession } from './end-session';
import { storageVisitMarker, type VisitMarker } from './first-visit';
import { signOutUrl } from './sign-out-url';

export interface Session {
  /** Signed in: the auth cookie is present, and `who` hasn't said otherwise. */
  signedIn: boolean;
  /** The signed-in player, once `who` has answered. */
  player?: Player;
  /** Whether this browser has seen the game before (the first-visit marker). */
  returningVisitor: () => boolean;
  /** Marks this browser as having seen the game. */
  markVisited: () => void;
  /**
   * Replaces this page with the identity app's sign-in, return_to set to this page. Replacing keeps the page out of
   * history, so the identity app's back chevron can't loop back to it.
   */
  goToSignIn: () => void;
  /**
   * Deletes the auth cookie, forgets what this device cached for the player, clears the session on the API, then goes
   * to the identity app's /sign-out. Signing out still completes when the API call fails.
   */
  signOut: () => Promise<void>;
}

export interface SessionDependencies {
  authCookie: AuthCookie;
  visits: VisitMarker;
  endSession: EndSession;
  browserLocation: BrowserLocation;
  /** The identity app's origin. */
  identityOrigin: string;
  /** Asks the API who is signed in. */
  useWho: (options: { skip: boolean }) => WhoAmIResult;
  /** Forgets what this device cached for the signed-in player: their account settings. */
  forgetPlayer: () => void;
}

const browserDependencies = (): SessionDependencies => ({
  authCookie: documentAuthCookie(),
  visits: storageVisitMarker(),
  endSession: createEndSession(),
  browserLocation: windowLocation,
  identityOrigin: IDENTITY_URL,
  useWho: useWhoAmIQuery,
  forgetPlayer: () => storageAccountSettingsCache().clear(),
});

const SessionContext = createContext<Session | undefined>(undefined);

export interface SessionProviderProps {
  children: React.ReactNode;
  /** Replaces the browser's cookie, storage, API and location. */
  dependencies?: Partial<SessionDependencies>;
}

/**
 * The words-happen session as the game app sees it. The game never loads Firebase: signed in means the auth cookie
 * is present, and `who` is the authority. When the cookie is present but `who` returns null, the cookie is deleted
 * and the player is signed out. When `who` returns a player, the browser is marked as a returning visitor.
 */
export const SessionProvider: React.FC<SessionProviderProps> = ({ children, dependencies }) => {
  const [deps] = useState(() => ({ ...browserDependencies(), ...dependencies }));
  const { authCookie, visits, endSession, browserLocation, identityOrigin, useWho, forgetPlayer } = deps;
  const [cookiePresent, setCookiePresent] = useState(() => authCookie.present());
  const { who, loading, error } = useWho({ skip: !cookiePresent });

  useEffect(() => {
    if (!cookiePresent || loading || error || who === undefined) {
      return;
    }
    if (who === null) {
      authCookie.remove();
      setCookiePresent(false);
    } else {
      visits.mark();
    }
  }, [cookiePresent, loading, error, who, authCookie, visits]);

  const signOut = useCallback(async () => {
    authCookie.remove();
    forgetPlayer();
    try {
      await endSession();
    } catch (cause) {
      logger.warn({ message: 'Clearing the session failed', error: (cause as Error).message });
    }
    browserLocation.assign(signOutUrl(identityOrigin));
  }, [authCookie, forgetPlayer, endSession, browserLocation, identityOrigin]);

  const goToSignIn = useCallback(() => {
    browserLocation.replace(
      identityAuthUrl(identityOrigin, IDENTITY_PATHS.signIn, { returnTo: browserLocation.href() }),
    );
  }, [browserLocation, identityOrigin]);

  const session = useMemo<Session>(
    () => ({
      signedIn: cookiePresent,
      player: cookiePresent && who ? who : undefined,
      returningVisitor: visits.returning,
      markVisited: visits.mark,
      goToSignIn,
      signOut,
    }),
    [cookiePresent, who, visits, goToSignIn, signOut],
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
};

export const useSession = (): Session => {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return session;
};
